// Flag or unflag an account High priority from any board or the sidebar. Optimistic: the cached
// book rows change on the click, and go back if the server refuses or the write fails.
//
// Both cached books are patched, the strategists' (`['book']`) and the AE's (`['ae-book']`), so the
// row reads the same on every page without a refetch. The server's answer then replaces the guess
// with who set it and when.

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { setAccountPriority } from '../api/client';
import type { TokenGetter } from '../api/client';
import type { BookAccount, BookPayload } from '../api/types';
import { isHighPriority, priorityFailureNote, priorityFieldsOf, withPriorityFields } from './priorityFlag';
import type { PriorityFields } from './priorityFlag';

const BOOK_QUERY_KEYS = [['book'], ['ae-book']];

function patchCachedBooks(queryClient: QueryClient, accountId: string, fields: PriorityFields) {
  for (const queryKey of BOOK_QUERY_KEYS) {
    queryClient.setQueryData<BookPayload>(queryKey, current => withPriorityFields(current, accountId, fields));
  }
}

export interface PriorityToggle {
  // The account whose write is in flight, so its toggle can show it is busy.
  pendingId: string | null;
  // The last failure, said in words; null after a success or a dismiss.
  note: string | null;
  toggle: (row: BookAccount) => void;
  dismissNote: () => void;
}

export function usePriorityToggle(getToken: TokenGetter): PriorityToggle {
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const toggle = useCallback((row: BookAccount) => {
    const accountId = row.id;
    const wanted = !isHighPriority(row);
    const previous = priorityFieldsOf(row);

    // The guess first, so the flag changes on the click.
    patchCachedBooks(queryClient, accountId, {
      high_priority: wanted,
      high_priority_set_by: wanted ? null : previous.high_priority_set_by,
      high_priority_set_at: wanted ? null : previous.high_priority_set_at,
    });
    setPendingId(accountId);
    setNote(null);

    // Then the server's answer replaces it, or the row goes back as it was.
    setAccountPriority(getToken, { account_id: accountId, high_priority: wanted })
      .then(result => {
        patchCachedBooks(queryClient, accountId, {
          high_priority: result.high_priority,
          high_priority_set_by: result.high_priority_set_by,
          high_priority_set_at: result.high_priority_set_at,
        });
      })
      .catch((error: unknown) => {
        console.error('high priority flag not saved; reverted.', { accountId, wanted, error });
        patchCachedBooks(queryClient, accountId, previous);
        setNote(priorityFailureNote(row.account, wanted, error instanceof Error ? error.message : ''));
      })
      .finally(() => setPendingId(current => (current === accountId ? null : current)));
  }, [getToken, queryClient]);

  const dismissNote = useCallback(() => setNote(null), []);
  return { pendingId, note, toggle, dismissNote };
}
